import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { workflowUploadApi } from "./workflow-upload.api";
import { useWorkflowUploadStore } from "./workflow-upload.store";

export const workflowUploadKeys = {
	all: ["workflow-upload"] as const,
	list: (workflowId: string) => [...workflowUploadKeys.all, "list", workflowId] as const,
};

export const useGetWorkflowUploads = (workflowId: string) => {
	const setUploads = useWorkflowUploadStore((state) => state.setUploads);

	const query = useQuery({
		queryKey: workflowUploadKeys.list(workflowId),
		queryFn: () => workflowUploadApi.list(workflowId),
		enabled: Boolean(workflowId),
	});

	useEffect(() => {
		if (query.data) setUploads(query.data.uploads);
	}, [query.data, setUploads]);

	return query;
};

export const useUploadWorkflowFile = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			workflowId,
			file,
			tag,
		}: {
			workflowId: string;
			file: File;
			tag?: string;
		}) => {
			const mimeType = file.type || "application/octet-stream";
			const presign = await workflowUploadApi.presignUpload(workflowId, {
				fileName: file.name,
				mimeType,
				tag,
			});

			await workflowUploadApi.uploadFile(presign.uploadUrl, file, mimeType);

			await workflowUploadApi.completeUpload(workflowId, presign.uploadId, file.size);

			return presign;
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: workflowUploadKeys.list(variables.workflowId),
			});
		},
	});
};

export const useGetDownloadUrl = () => {
	return useMutation({
		mutationFn: ({
			workflowId,
			uploadId,
		}: {
			workflowId: string;
			uploadId: string;
		}) => workflowUploadApi.getDownloadUrl(workflowId, uploadId),
	});
};

export const useDeleteWorkflowUpload = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			workflowId,
			uploadId,
		}: {
			workflowId: string;
			uploadId: string;
		}) => workflowUploadApi.delete(workflowId, uploadId),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: workflowUploadKeys.list(variables.workflowId),
			});
		},
	});
};
